import { createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import {
  clearAnalyticsCookies,
  ConsentChoice,
  loadAnalytics,
  readConsent,
  writeConsent,
} from '@/utils/consent/cookieConsent';
import { AppRoutes } from '@/utils/routes/router';

type CookieConsentContextValue = {
  consent: ConsentChoice | null;
  openSettings: () => void;
};

const CookieConsentContext = createContext<CookieConsentContextValue | null>(null);

function isMarketingPath(pathname: string) {
  return (
    pathname === '/' ||
    pathname === AppRoutes.landing ||
    pathname === AppRoutes.contactUs ||
    pathname === AppRoutes.cookies ||
    pathname === AppRoutes.riskAssessment ||
    pathname.startsWith('/waitlist')
  );
}

export function CookieConsentProvider({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const [consent, setConsent] = useState<ConsentChoice | null>(() => readConsent());
  const [settingsOpen, setSettingsOpen] = useState(false);

  useEffect(() => {
    if (consent === 'all') {
      loadAnalytics();
    }
  }, [consent]);

  const showBanner = settingsOpen || (consent === null && isMarketingPath(pathname));

  const value = useMemo<CookieConsentContextValue>(
    () => ({
      consent,
      openSettings: () => setSettingsOpen(true),
    }),
    [consent],
  );

  const choose = (choice: ConsentChoice) => {
    const previous = consent;
    writeConsent(choice);
    setConsent(choice);
    setSettingsOpen(false);
    if (choice === 'all') {
      loadAnalytics();
      return;
    }
    clearAnalyticsCookies();
    if (previous === 'all') {
      window.location.reload();
    }
  };

  return (
    <CookieConsentContext.Provider value={value}>
      {children}
      {showBanner ? (
        <div className="fixed inset-x-0 bottom-0 z-[10001] px-4 pb-4 pointer-events-none">
          <div
            className="pointer-events-auto mx-auto max-w-3xl rounded-2xl border p-5 shadow-2xl md:p-6"
            style={{
              backgroundColor: 'rgba(14, 28, 42, 0.96)',
              borderColor: 'rgba(250, 250, 250, 0.12)',
              boxShadow: '0 16px 48px rgba(0, 0, 0, 0.35)',
            }}
            role="dialog"
            aria-labelledby="cookie-consent-title"
          >
            <p id="cookie-consent-title" className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-accent">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
                <circle cx="12" cy="12" r="8.25" stroke="currentColor" strokeWidth="1.7" />
                <circle cx="9" cy="10" r="1.15" fill="currentColor" />
                <circle cx="13.5" cy="8.5" r="1" fill="currentColor" />
                <circle cx="14.2" cy="13.2" r="1.2" fill="currentColor" />
                <circle cx="9.4" cy="14.4" r="0.85" fill="currentColor" />
              </svg>
              Cookies
            </p>
            <div
              className="mt-3 h-[2px] w-full rounded-full"
              style={{
                background: 'linear-gradient(90deg, transparent, #ffbe00 16%, #00b9ff 84%, transparent)',
                boxShadow: '0 0 10px rgba(0, 185, 255, 0.35)',
              }}
              aria-hidden
            />
            <p className="mt-3 text-sm leading-relaxed text-secondary md:text-base">
              We use necessary cookies to run the site, keep you signed in, and protect the waitlist form. With your
              permission we also use analytics cookies to understand which pages are visited.
            </p>
            <div
              className="mt-4 h-[2px] w-full rounded-full"
              style={{
                background: 'linear-gradient(90deg, transparent, #00b9ff 18%, #ffbe00 82%, transparent)',
                boxShadow: '0 0 10px rgba(255, 190, 0, 0.28)',
              }}
              aria-hidden
            />
            <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <Link to={AppRoutes.cookies} className="text-sm font-medium text-accent hover:underline">
                Cookie details
              </Link>
              <div className="flex flex-col-reverse gap-2 sm:flex-row">
                <button
                  type="button"
                  onClick={() => choose('necessary')}
                  className="rounded-lg border border-[#00b9ff]/60 bg-[#00b9ff]/15 px-4 py-2.5 text-sm font-semibold text-[#f4fbff] transition-all hover:scale-[1.01] hover:border-[#00b9ff] hover:shadow-[0_0_16px_rgba(0,185,255,0.35)] active:scale-[0.99]"
                >
                  Necessary only
                </button>
                <button
                  type="button"
                  onClick={() => choose('all')}
                  className="rounded-lg border border-transparent bg-[#fafafa] px-4 py-2.5 text-sm font-semibold text-[#0e1c2a] transition-all hover:scale-[1.01] hover:border-[#00b9ff] hover:shadow-[0_0_16px_rgba(0,185,255,0.35)] active:scale-[0.99]"
                >
                  Allow all
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </CookieConsentContext.Provider>
  );
}

export function CookieSettingsLink({ className }: { className?: string }) {
  const context = useContext(CookieConsentContext);
  if (!context) {
    return null;
  }

  return (
    <button
      type="button"
      onClick={context.openSettings}
      className={`bg-transparent p-0 ${className ?? ''}`}
      style={{ color: 'inherit', font: 'inherit', cursor: 'pointer' }}
    >
      Cookie settings
    </button>
  );
}
